import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { isKeyed, isScored, rid } from "@/lib/questionRules";
import { Field } from "@/pages/assessments/builder/Field";

const QUIZ_KINDS = [["mcq", "Multiple choice"], ["short", "Short answer"]];
const CUSTOM_KINDS = [["yesno", "Yes / No"], ["mcq", "Multiple choice"], ["short", "Short text"], ["text", "Long text"]];
const opts = () => [{ id: rid(), text: "", correct: false }, { id: rid(), text: "", correct: false }];

function retype(q, k) {
  if (k === "mcq") return { ...q, kind: q.kind === "multi" ? "multi" : "single", options: ["single", "multi"].includes(q.kind) ? q.options : opts() };
  if (k === "yesno") return { ...q, kind: "yesno", options: [{ id: "yes", text: "Yes", correct: true }, { id: "no", text: "No", correct: false }] };
  return { ...q, kind: k, options: [] };
}

function Options({ q, set, errs, canEdit, scored }) {
  const multi = q.kind === "multi";
  const put = (i, patch) => set({ ...q, options: q.options.map((o, j) => (j === i ? { ...o, ...patch } : "correct" in patch && !multi && patch.correct ? { ...o, correct: false } : o)) });
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">Answer options{scored && <span className="font-normal text-ink-muted"> · tick the correct {multi ? "ones" : "one"}</span>}</legend>
      {q.kind !== "yesno" && <label className="flex items-center gap-2 text-sm"><Switch checked={multi} disabled={!canEdit} onCheckedChange={(v) => set({ ...q, kind: v ? "multi" : "single" })} data-testid="drawer-multi-toggle" /> Several answers can be correct</label>}
      {q.options.map((o, i) => (
        <div key={o.id} className="flex items-center gap-2">
          {scored && <input type={multi ? "checkbox" : "radio"} name="drawer-key" className="h-5 w-5" checked={!!o.correct} disabled={!canEdit} onChange={(e) => put(i, { correct: e.target.checked })} aria-label={`Answer ${i + 1} is correct`} data-testid={`drawer-correct-${i}`} />}
          <Input className="h-10 text-base" value={o.text} disabled={!canEdit || q.kind === "yesno"} onChange={(e) => put(i, { text: e.target.value })} placeholder={`Answer ${i + 1}`} data-testid={`drawer-option-${i}`} />
          {canEdit && q.kind !== "yesno" && q.options.length > 2 && <Button size="icon" variant="ghost" onClick={() => set({ ...q, options: q.options.filter((_, j) => j !== i) })} aria-label={`Remove answer ${i + 1}`}><X size={15} /></Button>}
        </div>
      ))}
      {canEdit && q.kind !== "yesno" && q.options.length < 8 && <Button size="sm" variant="ghost" onClick={() => set({ ...q, options: [...q.options, { id: rid(), text: "", correct: false }] })} data-testid="drawer-add-option"><Plus size={14} /> Add option</Button>}
      {(errs.options || errs.key) && <p className="text-sm text-risk" role="alert">{errs.options || errs.key}</p>}
    </fieldset>
  );
}

export function QuestionForm({ q, secType, onChange: set, errs, canEdit }) {
  const quiz = secType === "quiz";
  const scored = isScored(q, secType);
  const kinds = quiz ? QUIZ_KINDS : CUSTOM_KINDS;
  const cur = ["single", "multi"].includes(q.kind) ? "mcq" : q.kind;
  return (
    <div className="space-y-5" data-testid="question-form">
      <Field label="Question" error={errs.prompt} id="q-prompt">
        <Textarea id="q-prompt" rows={3} className="text-base" value={q.prompt} disabled={!canEdit} onChange={(e) => set({ ...q, prompt: e.target.value })} placeholder="What do you want to ask?" data-testid="drawer-q-prompt" />
      </Field>
      <div role="radiogroup" aria-label="Question type" className="inline-flex flex-wrap overflow-hidden rounded-full border border-line">
        {kinds.map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={cur === k} disabled={!canEdit} onClick={() => set(retype(q, k))} data-testid={`drawer-kind-${k}`}
            className={`px-3 py-1.5 text-sm transition-colors ${cur === k ? "bg-primary text-white" : "bg-white text-ink-secondary hover:bg-surface-2"}`}>{l}</button>
        ))}
      </div>
      {!quiz && (
        <div className="flex flex-wrap gap-5 text-sm">
          <label className="flex items-center gap-2"><Switch checked={q.scored} disabled={!canEdit} onCheckedChange={(v) => set({ ...q, scored: v })} data-testid="drawer-scored" /> {q.scored ? "Scored" : "Info only"}</label>
          <label className="flex items-center gap-2"><Switch checked={q.required} disabled={!canEdit} onCheckedChange={(v) => set({ ...q, required: v })} data-testid="drawer-required" /> Required</label>
        </div>
      )}
      {(isKeyed(q) || ["single", "multi"].includes(q.kind)) && <Options q={q} set={set} errs={errs} canEdit={canEdit} scored={scored} />}
      {!isKeyed(q) && scored && (
        <>
          <Field label="Model answer" error={errs.model} id="q-model" hint="Written answers are compared with this by meaning.">
            <Textarea id="q-model" rows={3} value={q.model_answer} disabled={!canEdit} onChange={(e) => set({ ...q, model_answer: e.target.value })} data-testid="drawer-model-answer" />
          </Field>
          <div className="grid gap-3 sm:grid-cols-3">
            {["1", "3", "5"].map((k) => (
              <Field key={k} label={`Score ${k} looks like`} id={`q-rubric-${k}`}>
                <Textarea id={`q-rubric-${k}`} rows={2} value={(q.rubric || {})[k] || ""} disabled={!canEdit} onChange={(e) => set({ ...q, rubric: { ...(q.rubric || {}), [k]: e.target.value } })} data-testid={`drawer-rubric-${k}`} />
              </Field>
            ))}
          </div>
        </>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {scored && <Field label="Points" id="q-points"><Input id="q-points" type="number" min={0.5} step={0.5} value={q.points} disabled={!canEdit} onChange={(e) => set({ ...q, points: Number(e.target.value) || 1 })} data-testid="drawer-points" /></Field>}
        <Field label="Competency (optional)" id="q-comp"><Input id="q-comp" value={q.competency || ""} disabled={!canEdit} onChange={(e) => set({ ...q, competency: e.target.value })} placeholder="e.g. Excel" data-testid="drawer-q-competency" /></Field>
      </div>
    </div>
  );
}
